import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import Button from "../../components/Button/Button";
import OffShiftBadge from "../../components/OffShiftBadge/OffShiftBadge";

import { useToast } from "../../context/ToastContext";
import { getColorBaseTokenValue } from "../../design-tokens";

import ProductionApi, { SynopticResponse, SynopticTrend } from "../../services/ProductionApi";
import StationStateApi from "../../services/StationStateApi";
import { normalizeStationState, StationState, Vaga } from "../../services/stationState.types";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import { formatElapsedClock } from "../../utilities/duration";
import SynopticTile from "./SynopticTile";
import styles from "./ReportSynoptic.module.css";
const CYCLE_STD_FALLBACK = 2334;

/** Status derivado de um elevador (per-vaga occupancy + relógio) ou motivo de parada da estação. */
type ElevatorStatus = "Produzindo" | "CicloExcedido" | "Ausencia" | "FaltaMaterial" | "Livre";

const codeOf = (vagaKey: string): string => vagaKey.replace(/^vaga_?/i, "");

const ReportSynoptic: React.FC = () => {
  const { t, i18n } = useTranslation("synoptic");
  const { displayToast } = useToast();
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";

  const [syn, setSyn] = useState<SynopticResponse | null>(null);
  const [st, setSt] = useState<StationState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [paused, setPaused] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [now, setNow] = useState<Date>(() => new Date());

  const fmt = useCallback(
    (v: number, dec = 0): string =>
      Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec }),
    [locale],
  );
  const signed = useCallback((v: number, dec = 0): string => `${v > 0 ? "+" : ""}${fmt(v, dec)}`, [fmt]);
  // Tempo como relógio HH:MM:SS (mesmo formato do Mapa de Ocupação; a partir de 24h
  // ganha o prefixo de dias — "2D 10:17:33"). null → "—".
  const fmtClock = useCallback(
    (sec: number | null): string => (sec == null ? "—" : formatElapsedClock(sec)),
    [],
  );

  // Notifica erro só na TRANSIÇÃO saudável→erro (evita toast a cada 20 s durante uma queda).
  const hadErrorRef = useRef(false);
  const load = useCallback(
    async (isCancelled?: () => boolean): Promise<void> => {
      const cancelled = (): boolean => (isCancelled ? isCancelled() : false);
      try {
        // allSettled: uma fonte fora do ar NÃO derruba a outra (render parcial via hasSyn/hasSt).
        const [synR, stR] = await Promise.allSettled([
          ProductionApi.GetSynoptic(),
          StationStateApi.GetStationState(),
        ]);
        if (cancelled()) return;
        if (synR.status === "fulfilled") setSyn((synR.value?.data as SynopticResponse) ?? null);
        if (stR.status === "fulfilled") setSt(normalizeStationState(stR.value?.data) ?? null);

        if (synR.status === "fulfilled" || stR.status === "fulfilled") {
          setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
          hadErrorRef.current = false;
        } else if (!hadErrorRef.current) {
          hadErrorRef.current = true;
          const reason = (synR as PromiseRejectedResult).reason ?? (stR as PromiseRejectedResult).reason;
          displayToast("error", t("loadErrorTitle"), reason?.errors?.[0] ?? t("loadErrorMsg"), false, 5000);
        }
      } finally {
        if (!cancelled()) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [displayToast, t, locale],
  );

  // Polling desacoplado da identidade de `load` (troca de idioma não reinicia a cadência).
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  // Congela pushes quando pausado (guarda o setState de um fetch em voo).
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  // Paint inicial.
  useEffect(() => {
    void loadRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualização em tempo real via SignalR (sem polling, sem botão); pausar desconecta e congela.
  useLiveRefresh(() => void loadRef.current(() => pausedRef.current), !paused);

  // Relógio de parede (1 s).
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Retry do estado de erro ignora o pause (não há dado para congelar).
  const forceReload = (): void => {
    setRefreshing(true);
    void loadRef.current();
  };

  const stateColor = useMemo(
    (): Record<ElevatorStatus, string> => ({
      Produzindo: getColorBaseTokenValue("color.feedback.success.500"),
      CicloExcedido: getColorBaseTokenValue("color.feedback.warning.700"), // Amarelo 700 — distinto de "Ausência"
      Ausencia: "#FF0000", // exibido como "Parada"
      FaltaMaterial: getColorBaseTokenValue("color.feedback.warning.500"), // Amarelo 500 — exibido como "Ausência"
      Livre: getColorBaseTokenValue("color.neutral.500"),
    }),
    [],
  );
  const stateLabel = useCallback(
    (s: ElevatorStatus): string =>
      ({
        Produzindo: t("stProduzindo"),
        CicloExcedido: t("stExcedido"),
        Ausencia: t("stAusencia"),
        FaltaMaterial: t("stMaterial"),
        Livre: t("stLivre"),
      })[s],
    [t],
  );

  const trendTitle = useCallback(
    (trend: SynopticTrend): string =>
      trend === "Up" ? t("trendUp") : trend === "Down" ? t("trendDown") : trend === "Flat" ? t("trendFlat") : t("trendNone"),
    [t],
  );

  const clockText = now.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const accents = useMemo(
    () => ({
      hourly: getColorBaseTokenValue("color.secondary.500"),
      daily: getColorBaseTokenValue("color.primary.500"),
      cycle: getColorBaseTokenValue("color.feedback.warning.500"),
      vehicles: getColorBaseTokenValue("color.secondary.700"),
    }),
    [],
  );

  const d = useMemo(() => {
    if (!syn && !st) return null;

    // ---- Produção / ciclo (synoptic) ----
    const dailyAttain = syn && syn.daily.target > 0 ? (syn.daily.producedCount / syn.daily.target) * 100 : 0;
    const cycleMin = syn && syn.cycle.avgCycleSeconds != null ? syn.cycle.avgCycleSeconds / 60 : null;
    const stdMin = syn ? syn.cycle.standardCycleSeconds / 60 : CYCLE_STD_FALLBACK / 60;
    const cycleStdSec = syn?.cycle.standardCycleSeconds ?? CYCLE_STD_FALLBACK;

    // ---- Ocupação / vagas (StationState) ----
    const vagas = st?.vagas ?? [];
    const byKey = (a: Vaga, b: Vaga): number => a.vagaKey.localeCompare(b.vagaKey);
    const elevators = vagas.filter((v) => v.type === "Operacao").sort(byKey);
    const supers = vagas.filter((v) => v.type === "Supermercado").sort(byKey);
    const occupied = vagas.filter((v) => v.state === "Ocupada");
    const stationState = st?.state ?? "";

    const elevStatus = (v: Vaga): ElevatorStatus => {
      if (v.state === "Ocupada") {
        return v.elapsedSeconds != null && v.elapsedSeconds > cycleStdSec ? "CicloExcedido" : "Produzindo";
      }
      // Livre: herda o motivo de parada da estação quando nada está produzindo.
      if (stationState === "FaltaMaterial" || stationState === "Ausencia") return stationState as ElevatorStatus;
      return "Livre";
    };
    const elevList = elevators.map((v) => ({ vaga: v, status: elevStatus(v) }));
    // Conta os que estão de fato "Produzindo" (exclui ciclo excedido) — casa com o rótulo e os tiles.
    const producing = elevList.filter((e) => e.status === "Produzindo").length;

    const smkOcc = supers.filter((v) => v.state === "Ocupada");
    // Só slots com permanência conhecida entram na média/pico (null = desconhecido, não zero).
    const smkTimed = smkOcc.filter((v) => v.elapsedSeconds != null);
    const smkAvgSec = smkTimed.length
      ? smkTimed.reduce((a, v) => a + (v.elapsedSeconds as number), 0) / smkTimed.length
      : null;
    const smkOldestSec = smkTimed.length ? Math.max(...smkTimed.map((v) => v.elapsedSeconds as number)) : null;

    const vehicles = occupied.length;
    const capacity = vagas.length;
    const idadeSec = occupied.reduce((a, v) => a + (v.elapsedSeconds ?? 0), 0);

    return {
      hasSyn: !!syn,
      hasSt: !!st,
      dailyAttain,
      cycleMin,
      stdMin,
      elevList,
      producing,
      supers,
      smkOcc,
      smkAvgSec,
      smkOldestSec,
      vehicles,
      capacity,
      idadeSec,
    };
  }, [syn, st]);

  const attainColor =
    d && d.dailyAttain >= 100
      ? getColorBaseTokenValue("color.feedback.success.500")
      : getColorBaseTokenValue("color.secondary.500");
  const capColor = getColorBaseTokenValue("color.secondary.500");
  const smkColor = getColorBaseTokenValue("color.secondary.500");

  const legend: ElevatorStatus[] = ["Produzindo", "CicloExcedido", "Ausencia", "FaltaMaterial", "Livre"];

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <div className={styles.headerControls}>
          <OffShiftBadge />
          <span className={`${styles.livePill} ${paused ? styles.livePillPaused : ""}`}>
            <span className={`${styles.liveDot} ${paused ? styles.liveDotPaused : ""}`} />
            {paused ? t("paused") : t("live")}
          </span>
          <span className={styles.clock}>
            <span className="material-symbols-outlined">schedule</span>
            {clockText}
          </span>
          <Button
            label={paused ? t("resume") : t("pause")}
            icon={paused ? "play_arrow" : "pause"}
            severity="secondary"
            variant="outlined"
            onClick={() => setPaused((p) => !p)}
          />
        </div>
      </PageHeader>

      {loading && !d ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
        </div>
      ) : !d ? (
        <div className={styles.card}>
          <div className={styles.errorBox}>
            <span className={`material-symbols-outlined ${styles.errorIcon}`}>sync_problem</span>
            <div className={styles.errorTitle}>{t("errorTitle")}</div>
            <Button label={t("retry")} icon="refresh" severity="secondary" variant="outlined" onClick={forceReload} loading={refreshing} />
          </div>
        </div>
      ) : (
        <>
          {/* Grade única: 1ª linha = 4 tiles; 2ª linha = elevadores · supermercado · idade (alinhadas) */}
          <div className={styles.grid8}>
            <SynopticTile
              icon="schedule"
              accent={accents.hourly}
              label={t("tHourly")}
              value={syn ? fmt(syn.hourly.producedCount) : "—"}
              sub={syn ? `${t("target")} ${fmt(syn.hourly.target, 1)}/${t("perHour")}` : undefined}
              trend={syn?.hourly.trend}
              higherBetter
              trendTitle={syn ? trendTitle(syn.hourly.trend) : undefined}
            />
            <SynopticTile
              icon="inventory_2"
              accent={accents.daily}
              label={t("tDaily")}
              value={syn ? fmt(syn.daily.producedCount) : "—"}
              sub={
                syn ? (
                  <>
                    <div>
                      <span className={styles.tileSubStrong}>{fmt(d.dailyAttain, 0)}%</span> {t("ofTarget")} · {t("target")}{" "}
                      {fmt(syn.daily.target, 0)} · {signed(syn.daily.variance)}
                    </div>
                    <div className={styles.attainBar} style={{ marginTop: 6 }}>
                      <span
                        className={styles.attainFill}
                        style={{ width: `${Math.max(0, Math.min(100, d.dailyAttain))}%`, background: attainColor }}
                      />
                    </div>
                  </>
                ) : undefined
              }
              trend={syn?.daily.trend}
              higherBetter
              trendTitle={syn ? trendTitle(syn.daily.trend) : undefined}
            />
            <SynopticTile
              icon="timer"
              accent={accents.cycle}
              label={t("tCycle")}
              value={d.cycleMin != null ? fmtClock(syn?.cycle.avgCycleSeconds ?? null) : "—"}
              sub={
                d.cycleMin != null
                  ? `${t("vsStd")} ${fmtClock(syn ? syn.cycle.standardCycleSeconds : null)}`
                  : t("noCycles")
              }
              trend={syn?.cycle.trend}
              higherBetter={false}
              trendTitle={syn ? trendTitle(syn.cycle.trend) : undefined}
            />
            <SynopticTile
              icon="directions_car"
              accent={accents.vehicles}
              label={t("tVehicles")}
              value={d.hasSt ? fmt(d.vehicles) : "—"}
              sub={
                d.hasSt ? (
                  <>
                    <div>
                      {fmt(d.vehicles)} / {fmt(d.capacity)} {t("capacity")}
                    </div>
                    <div className={styles.attainBar} style={{ marginTop: 6 }}>
                      <span
                        className={styles.attainFill}
                        style={{ width: `${d.capacity > 0 ? (d.vehicles / d.capacity) * 100 : 0}%`, background: capColor }}
                      />
                    </div>
                  </>
                ) : undefined
              }
            />
          {d.hasSt && (
            <>
                {/* Elevadores */}
                <section className={`${styles.card} ${styles.spanElev}`}>
                  <div className={styles.occHeader}>
                    <span className={styles.occTitle}>
                      <span className="material-symbols-outlined">elevator</span>
                      {t("elevators")}
                    </span>
                    <span className={styles.occSummary}>
                      <span className={styles.occSummaryStrong}>{fmt(d.producing)}</span> / {fmt(d.elevList.length)}{" "}
                      {t("producingLower")}
                    </span>
                  </div>
                  <div className={styles.elevGrid}>
                    {d.elevList.map(({ vaga, status }) => (
                      <div key={vaga.vagaKey} className={styles.elevItem}>
                        <div className={styles.elevItemHead}>
                          <span className={styles.elevName}>
                            {t("elevator")} {codeOf(vaga.vagaKey)}
                          </span>
                          <span className={styles.elevDot} style={{ background: stateColor[status] }} />
                        </div>
                        <span
                          className={styles.stateTag}
                          style={{ background: `${stateColor[status]}22`, color: stateColor[status] }}
                        >
                          {stateLabel(status)}
                        </span>
                        <span className={styles.elevSince}>
                          {vaga.elapsedSeconds != null
                            ? `${t("inStateFor")} ${fmtClock(vaga.elapsedSeconds)}`
                            : t("noEvent")}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Supermercado */}
                <section className={`${styles.card} ${styles.spanSmk}`}>
                  <div className={styles.occHeader}>
                    <span className={styles.occTitle}>
                      <span className="material-symbols-outlined">inventory_2</span>
                      {t("supermarket")}
                    </span>
                    <span className={styles.occSummary}>
                      <span className={styles.occSummaryStrong}>{fmt(d.smkOcc.length)}</span> / {fmt(d.supers.length)}{" "}
                      {t("slotsLower")}
                    </span>
                  </div>
                  <div className={styles.smkBody}>
                    <div className={styles.smkSlots}>
                      {d.supers.map((v) => {
                        const occ = v.state === "Ocupada";
                        return (
                          <span
                            key={v.vagaKey}
                            className={`${styles.smkSlot} ${occ ? styles.smkSlotOn : ""}`}
                            style={occ ? { borderColor: smkColor, background: `${smkColor}18`, color: smkColor } : undefined}
                            title={
                              occ && v.elapsedSeconds != null
                                ? `${codeOf(v.vagaKey)} · ${fmtClock(v.elapsedSeconds)}`
                                : codeOf(v.vagaKey)
                            }
                          >
                            {codeOf(v.vagaKey)}
                          </span>
                        );
                      })}
                    </div>
                    <div className={styles.smkStats}>
                      <div>
                        {t("avgStay")}:{" "}
                        <span className={styles.smkStatStrong}>{fmtClock(d.smkAvgSec)}</span>
                      </div>
                      <div>
                        {t("oldestSlot")}:{" "}
                        <span className={styles.smkStatStrong}>{fmtClock(d.smkOldestSec)}</span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Idade acumulada */}
                <section className={`${styles.card} ${styles.spanAge}`}>
                  <div className={styles.occHeader}>
                    <span className={styles.occTitle}>
                      <span className="material-symbols-outlined">hourglass_bottom</span>
                      {t("accumAge")}
                    </span>
                  </div>
                  <div className={styles.ageBody}>
                    <div className={styles.ageValue}>{d.vehicles > 0 ? fmtClock(d.idadeSec) : "—"}</div>
                    <div className={styles.ageCaption}>{t("accumAgeSub")}</div>
                  </div>
                </section>
            </>
          )}
          </div>

          {/* Legenda de estados */}
          {d.hasSt && (
            <div className={styles.stateLegend}>
              <span className={styles.stateLegendTitle}>{t("statesLegend")}</span>
              {legend.map((s) => (
                <span key={s} className={styles.legItem}>
                  <span className={styles.legDot} style={{ background: stateColor[s] }} />
                  {stateLabel(s)}
                </span>
              ))}
            </div>
          )}

          <div className={styles.tileSub} style={{ paddingLeft: 4 }}>
            {paused ? t("valuesFrozen") : t("realtime")} · {t("updatedAt")} {lastUpdated}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportSynoptic;
